import React, {useState} from 'react';
import errorHandler from '../../util/errorHandler.js';
import {MainContent} from '../../shared/grid/main-content/MainContentFeatures.js';
import {Btn} from '../../shared/elements/btn/Btn';
import styled from 'styled-components';
import {withTranslation} from 'react-i18next';
import CustomerInfo from './customers/CustomerInfo.jsx';
import CustomerList from './customers/CustomerList.jsx';
import {scrollSetup} from '../../shared/custom-features/scroll/scroll.js';
import AddCustomerModal from './customers/AddCustomerModal.jsx';
import EditCustomerModal from './customers/EditCustomerModal.jsx';
import useCustomers from './customers/CustomersState.js';
import _ from 'lodash';
import {validatedValues} from './customers/helper.js';
import {createCustomer} from './customers/endpoints.js';
import WarehousingTimeline from './timeline/Timeline.jsx';
import DownloadCustomersModal from './customers/DownloadCustomersModal.jsx';
import {Tab} from '../../shared/elements/tab/Tab.js';

function Warehousing({t}) {
    const {loading, customers, reload} = useCustomers(t, _, 10000);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [addCustomerModal, setAddCustomerModal] = useState(null);
    const [editCustomerModal, setEditCustomerModal] = useState(null);
    const [downloadCustomersModalShown, setDownloadCustomersModalShown] = useState(false);

    const submitCustomer = (values, options) => {
        createCustomer(validatedValues(values))
            .then((res) => {
                setSelectedCustomer(res.data);
                options.setSubmitting(false);
                reload();
                setAddCustomerModal(null);
            })
            .catch((e) => {
                options.setSubmitting(false);
                errorHandler(e.response.data, true, t);
                setAddCustomerModal(null);
                reload();
            });
    };

    return (
        <>
            {loading ? (
                <>{t('general.Loading')}</>
            ) : (
                <WarehousingContainer>
                    <MainContent.Body>
                        <WarehousingBody>
                            <SidebarContainer>
                                <Wrapper>
                                    <HeaderWrapper>
                                        <SidebarBtn
                                            type="button"
                                            color="primary"
                                            onClick={() => setAddCustomerModal(true)}
                                        >
                                            {t('customer.AddCustomer')}
                                        </SidebarBtn>
                                    </HeaderWrapper>
                                    <CustomerList
                                        customers={customers}
                                        setSelected={setSelectedCustomer}
                                        selected={selectedCustomer}
                                    />
                                    <FooterWrapper>
                                        <SidebarBtn
                                            type="button"
                                            color="primary"
                                            onClick={() => setDownloadCustomersModalShown(true)}
                                        >
                                            {t('customer.ExportAllCustomers')}
                                        </SidebarBtn>
                                    </FooterWrapper>
                                </Wrapper>
                            </SidebarContainer>

                            <InfoContainer>
                                <div>
                                    <h2>{t('general.Warehousing')}</h2>
                                    <CustomerInfo
                                        selectedCustomer={selectedCustomer}
                                        setEditCustomerModal={setEditCustomerModal}
                                    />
                                </div>
                            </InfoContainer>

                            <TimelineContainer>
                                <WarehousingTimeline></WarehousingTimeline>
                            </TimelineContainer>
                        </WarehousingBody>

                        {addCustomerModal && (
                            <AddCustomerModal cancel={() => setAddCustomerModal(null)} save={submitCustomer} />
                        )}
                        {editCustomerModal && (
                            <EditCustomerModal
                                customerId={editCustomerModal}
                                close={() => setEditCustomerModal(null)}
                                reload={reload}
                            ></EditCustomerModal>
                        )}
                        {downloadCustomersModalShown && (
                            <DownloadCustomersModal close={() => setDownloadCustomersModalShown(false)} />
                        )}
                    </MainContent.Body>
                </WarehousingContainer>
            )}
        </>
    );
}

const SidebarBtn = styled(Btn)`
    margin: 0;
`;
const WarehousingContainer = styled.div`
    background: #fafafa;
    ${MainContent.Container} {
        margin: calc(0px - var(--space-lg));
    }
`;
const WarehousingBody = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 0;
`;

const InfoContainer = styled.div`
    width: 100%;
    height: calc(100vh - 80px);
    padding: var(--space-lg) 0;
    overflow: hidden;
    > div {
        overflow: auto;
        padding: 0 var(--space-lg);
        height: 100%;
        ${scrollSetup};
    }
`;

export const sidebarStyling = () => `
    --table-sidebar-width: 280px;

    width: var(--table-sidebar-width);
    background: white;
    border-right: 1px solid #F4F4F4;
    padding: 0
    overflow: hidden;
    height: calc(100vh - 80px);
    > div {
        overflow: auto;
        height: 100%;
    }
`;

const SidebarContainer = styled.div`
    ${sidebarStyling}
`;

const TimelineContainer = styled(SidebarContainer)`
    --table-sidebar-width: 600px;
    border: none;
    border-left: 1px solid #f4f4f4;
`;
const HeaderWrapper = styled.div`
    padding: var(--space-lg) var(--space-lg) 0;
`;

const FooterWrapper = styled.div`
    padding: 0 var(--space-lg) var(--space-lg);
`;

const Wrapper = styled.div`
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: var(--space);

    ${HeaderWrapper},
    ${FooterWrapper} {
        flex-shrink: 1;
        ${Btn}, {
            margin: 0;
        }
    }
    ${Tab} {
        padding: 0 var(--space-lg);
        overflow-y: auto;
        ${scrollSetup};
        flex-grow: 1;
    }
`;

export default withTranslation()(Warehousing);
